import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import {
  Button,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
  StatusBadge,
} from '@/components/ui';
import { Download, Upload } from 'lucide-react';
import { adminFetch } from '@/lib/apiClient';
import { ApiClientError } from '@/lib/http-core';
import type { ProductsQueryParams } from '../hooks/useProductsQuery';
import type { ProductBulkResult } from '../hooks/useProductBulkMutation';
import {
  buildProductExportQuery,
  buildProductExportFullQuery,
  PRODUCT_EXPORT_FIELD_GROUPS,
  PRODUCT_IMPORT_TEMPLATE_PATH,
  PRODUCT_IMPORT_TEMPLATE_FULL_PATH,
  productImportTemplateFilename,
  productImportTemplateFullFilename,
} from '../lib/productExcel';
import { useExportColumnSelection } from '@/stores/useExportColumnSelection';
import { ExportJobError } from '@/lib/exportJobs';
import {
  criteriaFromProductQuery,
  PRODUCT_EXPORT_JOBS_PATH,
  useRequestProductExportMutation,
  type ProductExportType,
} from '../exportJobs/productExportJobs';

/**
 * 상품 전체필드 export 의 다운로드 필드 그룹 선택 localStorage 키(모달별 고유).
 * 항목(필드 그룹) 선택만 영속하며, 필수(always) 그룹은 항상 포함된다.
 */
const PRODUCT_EXPORT_FIELDS_STORAGE_KEY = 'product-export-field-groups';

/** 전체 필드 그룹 키(정본 순서). */
const PRODUCT_EXPORT_GROUP_KEYS = PRODUCT_EXPORT_FIELD_GROUPS.map((g) => g.key);

/** 현재 검색 필터를 운영자용 한국어 칩으로 요약(내부 enum 원시값 비노출). export 가 "화면에서 본 결과"임을 명시. */
const SALE_STATUS_LABEL: Record<string, string> = {
  ON_SALE: '판매중', SOLD_OUT: '품절', PRESALE: '예약판매', SALE_ENDED: '판매종료', HIDDEN: '비공개', PREPARING: '준비중',
};
const PRODUCT_TYPE_LABEL: Record<string, string> = {
  GENERAL: '일반', PRESALE: '예약', EVENT: '이벤트', SET: '세트', CONCERT: '콘서트',
};
const activeFilterChips = (f: ProductsQueryParams): string[] => {
  const chips: string[] = [];
  if (f.keyword?.trim()) chips.push(`검색어 "${f.keyword.trim()}"`);
  if (f.saleStatus) chips.push(`판매상태 ${SALE_STATUS_LABEL[f.saleStatus] ?? f.saleStatus}`);
  if (f.saleStatuses?.length) chips.push(`판매상태 ${f.saleStatuses.map((s) => SALE_STATUS_LABEL[s] ?? s).join('·')}`);
  if (f.productType) chips.push(`유형 ${PRODUCT_TYPE_LABEL[f.productType] ?? f.productType}`);
  if (f.categoryId) chips.push('카테고리 지정');
  if (f.productCode?.trim()) chips.push(`상품코드 ${f.productCode.trim()}`);
  if (f.barcode?.trim()) chips.push(`바코드 ${f.barcode.trim()}`);
  if (f.selfProductCode?.trim()) chips.push(`자체상품코드 ${f.selfProductCode.trim()}`);
  if (f.hasOption != null) chips.push(f.hasOption ? '옵션 있음' : '옵션 없음');
  if (f.displayed != null) chips.push(f.displayed ? '진열중' : '미진열');
  if (f.priceMin != null || f.priceMax != null) chips.push('가격 범위');
  if (f.stockMin != null || f.stockMax != null) chips.push('재고 범위');
  if (f.artistId) chips.push('아티스트 지정');
  if (f.hasArtist != null) chips.push(f.hasArtist ? '아티스트 연결' : '아티스트 미연결');
  if (f.createdFrom || f.createdTo) chips.push('등록일 범위');
  if (f.updatedFrom || f.updatedTo) chips.push('수정일 범위');
  return chips;
};

export interface ProductExcelDialogProps {
  open: boolean;
  onClose: () => void;
  /** 현재 목록 검색 필터 — export 가 "화면에서 본 결과"와 1:1이 되도록 그대로 전송. */
  listFilters: ProductsQueryParams;
  /** import 업로드 핸들러(9컬럼 일괄 변경, 상위가 mutation 보유). 결과는 result 로 받아 집계 표시. */
  onImport: (file: File) => Promise<ProductBulkResult>;
  /** #17 — 전체 필드 import 핸들러(상품코드 식별 CREATE/UPDATE). */
  onImportFull: (file: File) => Promise<ProductBulkResult>;
  importBusy?: boolean;
  /** #17 — 전체 필드 import 진행 상태. */
  importFullBusy?: boolean;
}

/**
 * 15-B-3 (ID90) — 상품 엑셀 다운로드/업로드 다이얼로그. export 는 orders OrderExportDialog 의 blob
 * 다운로드 패턴을 그대로 따른다(현재 필터로 GET → blob → 앵커 클릭). import 는 .xlsx 파일을 골라
 * 업로드 → 행별 부분 성공 결과를 집계 표시한다(round-trip: export → 편집 → import).
 */
export const ProductExcelDialog = ({
  open,
  onClose,
  listFilters,
  onImport,
  onImportFull,
  importBusy,
  importFullBusy,
}: ProductExcelDialogProps) => {
  const { t } = useTranslation('products');
  const fileRef = useRef<HTMLInputElement>(null);
  const fileFullRef = useRef<HTMLInputElement>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [downloadingFull, setDownloadingFull] = useState(false);
  const [downloadingTemplateFull, setDownloadingTemplateFull] = useState(false);
  // QA-199 — 전체필드 다운로드 필드 그룹 선택(상세HTML 등 무거운 열 제외 시 가속). 기본 전체 선택.
  // 2026-06-20: 필드 그룹 선택을 localStorage 에 영속(모달 재오픈/세션 간 직전 선택 유지).
  //   필수(always) 그룹은 렌더/쿼리에서 항상 포함되므로 영속 대상에서 빠져도 동작 동일.
  const { columns: exportGroups, toggleColumn: toggleExportGroupChecked } =
    useExportColumnSelection(PRODUCT_EXPORT_FIELDS_STORAGE_KEY, PRODUCT_EXPORT_GROUP_KEYS);
  // 기존 단일 인자(flip) 시그니처 유지 — 체크 상태는 현재 선택 여부로 도출.
  const toggleExportGroup = (key: string) =>
    toggleExportGroupChecked(key, !exportGroups.includes(key));
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProductBulkResult | null>(null);
  // 전체 필드 import 는 별도 결과 집계(일괄 변경 결과와 혼동 방지).
  const [resultFull, setResultFull] = useState<ProductBulkResult | null>(null);
  // 다운로드 요청(백그라운드 생성 → 상품 엑셀 다운로드 내역에서 받기). Idempotency-Key 는 대화상자를 열 때마다
  // 종류별로 새로 만들고 재시도에는 같은 키를 쓴다(두 번 눌러도 1건). 성공하면 그 종류의 키만 새로.
  const requestExport = useRequestProductExportMutation();
  const [requestedType, setRequestedType] = useState<ProductExportType | null>(null);
  const newKeys = () => ({ PRODUCT: crypto.randomUUID(), PRODUCT_FULL: crypto.randomUUID() });
  const [idempotencyKeys, setIdempotencyKeys] = useState<Record<ProductExportType, string>>(newKeys);
  useEffect(() => {
    if (open) {
      setRequestedType(null);
      setIdempotencyKeys(newKeys());
    }
  }, [open]);

  if (!open) return null;

  // GET → blob → 앵커 클릭 다운로드(export/템플릿 공용). 실패 시 호출부가 메시지 처리.
  // QA-199: 503(대용량 동기 생성 타임아웃 등) 시 raw "download failed: 503" 이 운영자에게
  // 그대로 노출되던 문제 → status 를 실은 ApiClientError 로 던져 friendlyMessage 가
  // 상태코드별 한국어 메시지(503="서비스를 일시적으로 사용할 수 없습니다…")로 매핑하게 한다.
  const downloadBlob = async (url: string, filename: string) => {
    const res = await adminFetch(url);
    if (!res.ok) {
      throw new ApiClientError(
        { code: 'PRODUCT_EXPORT_FAILED', message: '' },
        { status: res.status },
      );
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  /**
   * 다운로드 요청 — 즉시 엑셀(/export, /export-full)과 같은 조건(criteria)을 JSON 으로 보낸다. 400(검증)·404·409 는
   * BE 사유를 그대로, 그 밖에는 상태별 공통 문구.
   */
  const requestDownload = async (type: ProductExportType, query: string, fallback: string) => {
    setError(null);
    setRequestedType(null);
    const setBusy = type === 'PRODUCT' ? setDownloading : setDownloadingFull;
    setBusy(true);
    try {
      await requestExport.mutateAsync({
        type,
        criteria: criteriaFromProductQuery(query),
        idempotencyKey: idempotencyKeys[type],
      });
      setRequestedType(type);
      setIdempotencyKeys((k) => ({ ...k, [type]: crypto.randomUUID() }));
    } catch (err) {
      setError(
        err instanceof ExportJobError && err.showBeMessage
          ? err.message
          : friendlyMessage(
              err instanceof ExportJobError
                ? new ApiClientError({ code: err.code ?? 'PRODUCT_EXPORT_FAILED', message: '' }, { status: err.status })
                : err,
              fallback,
            ),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = () =>
    requestDownload(
      'PRODUCT',
      buildProductExportQuery(listFilters),
      t('products.excel.exportError', { defaultValue: '엑셀 다운로드 요청에 실패했습니다.' }),
    );

  // QA-90 — 업로드용 빈 양식(예시) 다운로드. 운영자가 받아 채워서 import 한다.
  const handleTemplateDownload = async () => {
    setError(null);
    setDownloadingTemplate(true);
    try {
      await downloadBlob(PRODUCT_IMPORT_TEMPLATE_PATH, productImportTemplateFilename());
    } catch (err) {
      setError(friendlyMessage(err, t('products.excel.templateError', { defaultValue: '업로드 양식 다운로드에 실패했습니다.' })));
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // 동일 파일 재선택도 동작하도록 input 값을 비운다.
    e.target.value = '';
    if (!file) return;
    setError(null);
    setResult(null);
    try {
      const res = await onImport(file);
      setResult(res);
    } catch (err) {
      setError(friendlyMessage(err, t('products.excel.importError', { defaultValue: '엑셀 업로드에 실패했습니다.' })));
    }
  };

  // #17 — 전체 필드 export / 양식 / import.
  const handleDownloadFull = () =>
    requestDownload(
      'PRODUCT_FULL',
      buildProductExportFullQuery(listFilters, exportGroups),
      t('products.excel.exportFullError', { defaultValue: '전체 필드 엑셀 요청에 실패했습니다.' }),
    );

  const handleTemplateDownloadFull = async () => {
    setError(null);
    setDownloadingTemplateFull(true);
    try {
      await downloadBlob(PRODUCT_IMPORT_TEMPLATE_FULL_PATH, productImportTemplateFullFilename());
    } catch (err) {
      setError(friendlyMessage(err, t('products.excel.templateFullError', { defaultValue: '전체 등록 양식 다운로드에 실패했습니다.' })));
    } finally {
      setDownloadingTemplateFull(false);
    }
  };

  const handleFileChangeFull = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    setResultFull(null);
    try {
      const res = await onImportFull(file);
      setResultFull(res);
    } catch (err) {
      setError(friendlyMessage(err, t('products.excel.importFullError', { defaultValue: '전체 필드 엑셀 업로드에 실패했습니다.' })));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (
          !o &&
          !importBusy &&
          !importFullBusy &&
          !downloading &&
          !downloadingTemplate &&
          !downloadingFull &&
          !downloadingTemplateFull
        ) {
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('products.excel.title', { defaultValue: '엑셀 일괄 작업' })}</DialogTitle>
          <DialogDescription>
            {t('products.excel.desc', {
              defaultValue: '현재 목록 필터에 해당하는 상품을 엑셀로 내려받아 편집한 뒤, 같은 양식으로 업로드하면 일괄 반영됩니다.',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-1">
          {requestedType ? (
            <div
              role="status"
              className="rounded-lg px-3 py-2 text-[13px]"
              style={{ background: 'var(--status-success-bg)', color: 'var(--status-success-fg)' }}
              data-testid="product-export-requested"
            >
              {t('products.excel.requested', { defaultValue: '요청했습니다. 엑셀 다운로드 내역에서 받으실 수 있습니다.' })}{' '}
              <Link to={PRODUCT_EXPORT_JOBS_PATH} className="font-medium underline" onClick={onClose}>
                {t('products.excel.goToJobs', { defaultValue: '다운로드 내역으로 가기' })}
              </Link>
            </div>
          ) : null}
          <section className="rounded-xl p-4" style={{ border: '1px solid var(--divider-soft)' }}>
            <h3 className="mb-1 text-xs font-semibold" style={{ color: 'var(--content-strong)' }}>
              {t('products.excel.exportTitle', { defaultValue: '다운로드' })}
            </h3>
            <p className="mb-3 text-xs" style={{ color: 'var(--content-muted)' }}>
              {t('products.excel.exportHint', {
                defaultValue: '현재 검색 필터에 해당하는 상품의 엑셀을 요청합니다. 만들어지면 엑셀 다운로드 내역에서 받습니다.',
              })}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void handleDownload()}
              disabled={downloading}
              data-testid="product-excel-download"
            >
              <Download size={14} aria-hidden="true" />
              {downloading
                ? t('products.excel.requesting', { defaultValue: '요청 중…' })
                : t('products.excel.requestDownload', { defaultValue: '상품 엑셀 다운로드 요청' })}
            </Button>
          </section>

          <section className="rounded-xl p-4" style={{ border: '1px solid var(--divider-soft)' }}>
            <h3 className="mb-1 text-xs font-semibold" style={{ color: 'var(--content-strong)' }}>
              {t('products.excel.importTitle', { defaultValue: '일괄 변경 업로드 (판매상태·진열·가격 등)' })}
            </h3>
            <p className="mb-3 text-xs" style={{ color: 'var(--content-muted)' }}>
              {t('products.excel.importHint', { defaultValue: '위 “엑셀 다운로드”로 받은 .xlsx 의 상품코드 열로 상품을 식별해 변경이 적용됩니다(빈 셀=미변경). 신규 상품 등록은 아래 “전체 필드” 양식을 사용하세요.' })}
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => void handleFileChange(e)}
              data-testid="product-excel-file"
            />
            <div className="flex flex-wrap items-center gap-2">
              {/* QA-90 — 업로드 예시(빈 양식) 다운로드. import 전 양식 확보용. */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void handleTemplateDownload()}
                disabled={downloadingTemplate}
                data-testid="product-excel-template"
              >
                <Download size={14} aria-hidden="true" />
                {downloadingTemplate
                  ? t('products.excel.downloading', { defaultValue: '다운로드 중…' })
                  : t('products.excel.templateDownload', { defaultValue: '업로드 양식 다운로드' })}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={importBusy}
                data-testid="product-excel-upload"
              >
                <Upload size={14} aria-hidden="true" />
                {importBusy
                  ? t('products.excel.uploading', { defaultValue: '업로드 중…' })
                  : t('products.excel.upload', { defaultValue: '엑셀 업로드' })}
              </Button>
            </div>

            {result ? (
              <div
                className="mt-3 flex flex-wrap items-center gap-2 text-xs"
                data-testid="product-excel-result"
              >
                <StatusBadge variant="success" label={t('products.bulkUpdate.resultSucceeded', { count: result.succeeded, defaultValue: `성공 ${result.succeeded}` })} />
                {result.skipped > 0 ? (
                  <StatusBadge variant="info" label={t('products.bulkUpdate.resultSkipped', { count: result.skipped, defaultValue: `스킵 ${result.skipped}` })} />
                ) : null}
                {result.failed.length > 0 ? (
                  <StatusBadge variant="warning" label={t('products.bulkUpdate.resultFailed', { count: result.failed.length, defaultValue: `실패 ${result.failed.length}` })} />
                ) : null}
              </div>
            ) : null}
            {result && result.failed.length > 0 ? (
              <ul className="mt-2 max-h-32 overflow-y-auto text-[11px]" style={{ color: 'var(--content-muted)' }}>
                {result.failed.slice(0, 20).map((f) => (
                  <li key={f.productId} className="truncate">
                    {f.productId}: {f.reason}
                  </li>
                ))}
              </ul>
            ) : null}
          </section>

          {/* #17 — 전체 필드 등록/수정. 상품코드 식별 CREATE/UPDATE, 다국어·가격·상세HTML·옵션 포함. */}
          <section className="rounded-xl p-4" style={{ border: '1px solid var(--divider-soft)' }}>
            <h3 className="mb-1 text-xs font-semibold" style={{ color: 'var(--content-strong)' }}>
              {t('products.excel.fullTitle', { defaultValue: '전체 필드 등록/수정' })}
            </h3>
            <p className="mb-3 text-xs leading-relaxed" style={{ color: 'var(--content-muted)' }}>
              {t('products.excel.fullHint', {
                defaultValue:
                  '상품코드로 식별합니다 — 없는 코드는 신규 등록, 있는 코드는 전체 교체됩니다. 컬럼: 상품코드·바코드·상품유형·판매상태·진열여부·대표카테고리ID·브랜드명·정가·판매가(KRW/USD/JPY/CNY)·상품명/설명/상세HTML(ko/en/ja/zh)·상품정보고시(ko)·옵션. 필수: 상품코드·대표카테고리ID·정가·판매가KRW·상품명_ko. 아티스트는 엑셀로 지정할 수 없습니다(상품 폼에서 연결).',
              })}
            </p>
            {/* 옵션 셀 형식(be-catalog — 옵션번호·자체옵션코드 포함). 형식은 서버가 정하고 BO 는 안내만 한다. */}
            <p className="mb-3 text-[11px] leading-relaxed" style={{ color: 'var(--content-muted)' }} data-testid="excel-option-hint">
              {t('products.excel.optionHint', {
                defaultValue:
                  '옵션 셀: 라벨:추가금:재고:옵션번호:자체옵션코드 를 ; 로 구분합니다(예: 김호중:0:7:V1352:A;김희재:0:0:V1353:). 옵션번호가 있으면 그 옵션을 수정하고, 비우면 새 옵션으로 추가합니다. 자체옵션코드를 비우면 지웁니다. 예전 3칸 형식(라벨:추가금:재고)도 올릴 수 있습니다. 옵션 라벨에 ; 가 들어간 상품은 엑셀 옵션 셀이 깨질 수 있으니 상품 화면에서 수정해 주세요.',
              })}
            </p>
            {/* QA-199 — 현재 검색 필터 요약(이 다운로드 = 화면에서 본 결과와 1:1). */}
            <div
              className="mb-3 rounded-lg p-2.5 text-[11px]"
              style={{ background: 'var(--surface-muted, #f6f6f7)', color: 'var(--content-muted)' }}
              data-testid="product-excel-filter-summary"
            >
              <span style={{ color: 'var(--content-strong)' }}>
                {t('products.excel.filterSummary', { defaultValue: '현재 검색 필터에 해당하는 상품만 내려받습니다' })}
              </span>
              {activeFilterChips(listFilters).length > 0 ? (
                <span className="ml-1 inline-flex flex-wrap gap-1 align-middle">
                  {activeFilterChips(listFilters).map((c) => (
                    <span
                      key={c}
                      className="rounded px-1.5 py-0.5"
                      style={{ background: 'var(--surface-default, #fff)', border: '1px solid var(--divider-soft)' }}
                    >
                      {c}
                    </span>
                  ))}
                </span>
              ) : (
                <span className="ml-1">
                  {t('products.excel.filterNone', { defaultValue: '(필터 없음 — 전체 상품, 최신순 최대 2,000건)' })}
                </span>
              )}
            </div>
            {/* QA-199 — 다운로드 필드 선택. 상세HTML 등 무거운 열을 빼면 생성·전송이 크게 빨라진다(전부 선택=라운드트립 양식). */}
            <fieldset className="mb-3 rounded-lg p-2.5" style={{ border: '1px solid var(--divider-soft)' }}>
              <legend className="px-1 text-[11px]" style={{ color: 'var(--content-muted)' }}>
                {t('products.excel.fieldSelect', { defaultValue: '다운로드 필드 (체크 해제 시 더 빠름)' })}
              </legend>
              <div className="flex flex-col gap-1.5">
                {PRODUCT_EXPORT_FIELD_GROUPS.map((g) => (
                  <label
                    key={g.key}
                    className="flex items-center gap-2 text-[11px]"
                    style={{ color: g.always ? 'var(--content-muted)' : 'var(--content-strong)' }}
                  >
                    <input
                      type="checkbox"
                      checked={g.always || exportGroups.includes(g.key)}
                      disabled={g.always}
                      onChange={() => toggleExportGroup(g.key)}
                      data-testid={`product-excel-field-${g.key}`}
                    />
                    {g.label}{g.always ? ' (필수)' : ''}
                  </label>
                ))}
              </div>
            </fieldset>
            <input
              ref={fileFullRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => void handleFileChangeFull(e)}
              data-testid="product-excel-full-file"
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void handleDownloadFull()}
                disabled={downloadingFull}
                data-testid="product-excel-full-download"
              >
                <Download size={14} aria-hidden="true" />
                {downloadingFull
                  ? t('products.excel.requesting', { defaultValue: '요청 중…' })
                  : t('products.excel.requestFull', { defaultValue: '전체필드 엑셀 요청' })}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void handleTemplateDownloadFull()}
                disabled={downloadingTemplateFull}
                data-testid="product-excel-full-template"
              >
                <Download size={14} aria-hidden="true" />
                {downloadingTemplateFull
                  ? t('products.excel.downloading', { defaultValue: '다운로드 중…' })
                  : t('products.excel.fullTemplateDownload', { defaultValue: '등록 양식 다운로드' })}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileFullRef.current?.click()}
                disabled={importFullBusy}
                data-testid="product-excel-full-upload"
              >
                <Upload size={14} aria-hidden="true" />
                {importFullBusy
                  ? t('products.excel.uploading', { defaultValue: '업로드 중…' })
                  : t('products.excel.fullUpload', { defaultValue: '전체 필드 업로드' })}
              </Button>
            </div>

            {resultFull ? (
              <div
                className="mt-3 flex flex-wrap items-center gap-2 text-xs"
                data-testid="product-excel-full-result"
              >
                <StatusBadge variant="success" label={t('products.bulkUpdate.resultSucceeded', { count: resultFull.succeeded, defaultValue: `성공 ${resultFull.succeeded}` })} />
                {resultFull.skipped > 0 ? (
                  <StatusBadge variant="info" label={t('products.bulkUpdate.resultSkipped', { count: resultFull.skipped, defaultValue: `스킵 ${resultFull.skipped}` })} />
                ) : null}
                {resultFull.failed.length > 0 ? (
                  <StatusBadge variant="warning" label={t('products.bulkUpdate.resultFailed', { count: resultFull.failed.length, defaultValue: `실패 ${resultFull.failed.length}` })} />
                ) : null}
              </div>
            ) : null}
            {resultFull && resultFull.failed.length > 0 ? (
              <ul className="mt-2 max-h-32 overflow-y-auto text-[11px]" style={{ color: 'var(--content-muted)' }}>
                {resultFull.failed.slice(0, 20).map((f) => (
                  <li key={f.productId} className="truncate">
                    {f.productId}: {f.reason}
                  </li>
                ))}
              </ul>
            ) : null}
          </section>

          {error ? (
            <p role="alert" className="text-xs" style={{ color: 'var(--status-danger-fg)' }}>
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button
              variant="ghost"
              size="sm"
              disabled={
                importBusy ||
                importFullBusy ||
                downloading ||
                downloadingTemplate ||
                downloadingFull ||
                downloadingTemplateFull
              }
            >
              {t('products.excel.close', { defaultValue: '닫기' })}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
